import { useState, useRef, useMemo } from "react";
import { Icon } from "@iconify/react";
import useCreateSchedule from "../hooks/useCreateSchedule";
import Toast from "./Toast";
import "../styles/ScheduleSelector.css";

const buildDaysFromScheduleOffers = (offers) => {
  if (!Array.isArray(offers) || offers.length === 0) return [];

  const groupedOffers = offers.reduce((acc, isoString) => {
    if (!isoString) return acc;

    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return acc;

    const dateKey = date.toISOString().split("T")[0];
    const time = date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (!acc[dateKey]) {
      acc[dateKey] = {
        day: `${dateKey}T00:00:00.000Z`,
        time: [],
      };
    }

    if (!acc[dateKey].time.includes(time)) {
      acc[dateKey].time.push(time);
    }

    return acc;
  }, {});

  return Object.values(groupedOffers)
    .map((day) => ({
      ...day,
      time: day.time.sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => new Date(a.day) - new Date(b.day));
};

function ScheduleSelector({ quote }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const { createSchedule, loading, error } = useCreateSchedule();
  const timesContainerRef = useRef(null);
  const confirmContainerRef = useRef(null);

  const operatingDays = quote.chosenOffer?.[0]?.carWorkshop?.operatingDays || [];
  const scheduleOfferDays = useMemo(
    () => buildDaysFromScheduleOffers(quote?.scheduleOffers),
    [quote?.scheduleOffers]
  );
  const availableDays = scheduleOfferDays.length > 0 ? scheduleOfferDays : operatingDays;

  if (availableDays.length === 0) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (scheduleOfferDays.length > 0) {
      date.setDate(date.getDate() + 1);
    }
    return date.toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleDateSelect = (day) => {
    setSelectedDate(day);
    setSelectedTime(null);
    setIsOpen(true);
    setTimeout(() => {
      timesContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setTimeout(() => {
      confirmContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const handleConfirm = async () => {
    if (selectedDate && selectedTime) {
      const dateTime = new Date(selectedDate.day);
      const [hours, minutes] = selectedTime.split(":");
      dateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const result = await createSchedule({
        webappQuoteUuid: quote.uuid,
        chosenCarWorkShopUuid: quote.chosenOffer?.[0]?.carWorkshop?.uuid,
        date: dateTime.toISOString(),
      });

      if (result.success) {
        setToast({
          message: `Agendamento confirmado para ${formatDate(selectedDate.day)} às ${selectedTime}`,
          type: "success"
        });
        
        setSelectedDate(null);
        setSelectedTime(null);
        setIsOpen(false);
      } else {
        setToast({
          message: `Erro ao confirmar agendamento: ${result.error}`,
          type: "error"
        });
      }
    }
  };

  return (
    <div className="schedule-selector">
      <div className="schedule-selector-header">
        <Icon icon="mdi:calendar-clock" className="schedule-icon" />
        <h2 className="schedule-title">Selecione a Data e Horário</h2>
      </div>

      <div className="schedule-dates-grid">
        {availableDays.map((day, index) => (
          index < 10 && <button
            key={index}
            className={`date-button ${selectedDate?.day === day.day ? "selected" : ""}`}
            onClick={() => handleDateSelect(day)}
          >
            <Icon icon="mdi:calendar" className="date-icon" />
            <span className="date-text">{formatDate(day.day)}</span>
          </button>
        ))}
      </div>

      {selectedDate && (
        <div ref={timesContainerRef} className={`schedule-times-container ${isOpen ? "open" : ""}`}>
          <div className="schedule-times-header">
            <Icon icon="mdi:clock-outline" className="time-header-icon" />
            <h3 className="schedule-times-title">Horários Disponíveis</h3>
          </div>
          
          <div className="schedule-times-grid">
            {selectedDate.time.map((time, index) => (
              <button
                key={index}
                className={`time-button ${selectedTime === time ? "selected" : ""}`}
                onClick={() => handleTimeSelect(time)}
              >
                <Icon icon="mdi:clock-time-four-outline" className="time-icon" />
                <span className="time-text">{time}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedDate && selectedTime && (
        <div ref={confirmContainerRef} className="schedule-confirm-container">
          <div className="schedule-summary">
            <Icon icon="mdi:information-outline" className="summary-icon" />
            <div className="summary-content">
              <p className="summary-label">Agendamento selecionado:</p>
              <p className="summary-value">
                {formatDate(selectedDate.day)} às {selectedTime}
              </p>
            </div>
          </div>
          
          <button 
            className="confirm-button" 
            onClick={handleConfirm}
            disabled={loading}
          >
            <Icon icon="mdi:check-circle" className="confirm-icon" />
            {loading ? "Confirmando..." : "Confirmar Agendamento"}
          </button>
          {error && (
            <div className="schedule-error">
              <Icon icon="mdi:alert-circle" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
      
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default ScheduleSelector;
